/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomáš Burian (burian@cadwork.cz)
 *
 */

import ViewControls from "App/view/controls/view_controls";
import RenderDocument from "App/render_document";
import View from "App/view/view";
import Camera from "App/view/cameras/camera";
import {vec3} from "gl-matrix";
import {degToRad} from "App/math";
import Messenger from "App/messenger";
import BoundingBox from "App/utils/bounding_box";
import {EViewControls, EViewControlsFamily} from "js_interface/src/modules/view_controls/scene_view_controls_enums";


export default
class FlyControls extends ViewControls {

    public static SPEED_STEPS_MIN: number = 0;
    public static SPEED_STEPS_MAX: number = 12;
    public static TOUCH_WALK_TIME_DELAY: number = 500;  // after this time camera starts move forward if touch coords are the same or in defined radius
    public static TOUCH_WALK_TOUCH_RADIUS: number = 20; // limit to handle touch as the same as the starting coordinates

    public static VIEW_ANGLE_MIN: number = 45;
    public static VIEW_ANGLE_MAX: number = 90;
    public static MOUSE_SENSITIVITY_MIN: number = 1;
    public static MOUSE_SENSITIVITY_MAX: number = 5;

    private avatarHeight: number = 0.5;
    private avatarWidth: number = 0.5;
    private _collisionCameraDown: Camera;
    private moveFlagMap: any = {};
    private PICK_DOWN_NEAR_VALUE: number = 0.01;
    private CDWK_FP_MODE_SPEED: number = 0.5;
    private speedMax: number = 0;
    private speedValues: number[];
    private _stepFactor: number = 0.1;
    private prevMouseWheelTime: number = 0;
    private _touchCoords: { x: number; y: number };
    private _acceleration: number = 0;
    private _lastTick: number = 0;
    private dx: vec3 = vec3.create();
    private dy: vec3 = vec3.create();
    private _touchDoubleTapTimeout: number = null;
    private speed: number = 0;

    constructor(aDocument: RenderDocument, aView: View) {
        super(aDocument, aView);

        this._allowLogDepth = true;
        this._useLogDepth = true;
        this._family = EViewControlsFamily.FLY;

        this._camera = new Camera(this._view);
        this._initCameraParams();

        // down collision camera and its settings
        this._collisionCameraDown = this._camera.clone();
        this._collisionCameraDown.setCameraUpVector(vec3.fromValues(0, 0, 1));
        // this is needed because when the camera is high above the underlying geometry, then the depth buffer resolution is not sufficient to pick exact point
        this._collisionCameraDown.setFieldOfView(0.0001);

        this.moveFlagMap = [];
        this.moveFlagMap[this.goForward] = false;
        this.moveFlagMap[this.goBack] = false;
        this.moveFlagMap[this.goLeft] = false;
        this.moveFlagMap[this.goRight] = false;
        this.moveFlagMap[this.goUp] = false;
        this.moveFlagMap[this.goDown] = false;

        this.CDWK_FP_MODE_NUM_STEPS = 12;	// Number of steps (mouse wheel scrolls) to get to max speed.
        this.CDWK_FP_MODE_MIN_MAX_SPEED = 40.0;	// Minimal maximal speed (to be able to move fast even in small models)
        this.CDWK_FP_MODE_MAX_SPEED_RATIO = 0.1;	// Maximal speed ratio to compute max speed

        /* Speed values table: when mode bbox is large, the upper part is used as well
         * This way it enables flying through a large landscape.
         */
        this.speedValues = [this.CDWK_FP_MODE_NUM_STEPS];
        this.speedValues[0] = 0.0;
        this.speedValues[1] = 0.1;
        this.speedValues[2] = 0.3;
        this.speedValues[3] = 0.5;
        this.speedValues[4] = 1.0;
        this.speedValues[5] = 2.0;
        this.speedValues[6] = 4.0;
        this.speedValues[7] = 8.0;
        this.speedValues[8] = 16.0;
        this.speedValues[9] = 32.0;
        this.speedValues[10] = 64.0;
        this.speedValues[11] = 100.0;
        this.step = 0;

        this.setSpeed(this.step);

        // forward camera and its settings derived form camera with opening angle set (compared to down collision camera)
        this._collisionCameraForward = this._camera.clone();
        this._collisionCameraForward.forceNearValue(0.01); // 0.01 is empiric value

        // this is a 10% behind the maximum flying step which is 100.0
        this._collisionCameraForward.forceFarValue(this.speedValues[FlyControls.SPEED_STEPS_MAX - 1] * this._stepFactor * 1.1);

        // Touch events
        this._touchCoords = {x: 0, y: 0};

        // aView.getControls().checkAndInitLogarithmicDepthBuffer();
        this.checkAndInitLogarithmicDepthBuffer();

        this.emitSpeed();
    }

    public static getSpeedLimits(): {min: number, max: number} {
        return {
            min: FlyControls.SPEED_STEPS_MIN,
            max: FlyControls.SPEED_STEPS_MAX
        };
    }

    public static getViewAngleLimits(): {min: number, max: number} {
        return {
            min: FlyControls.VIEW_ANGLE_MIN,
            max: FlyControls.VIEW_ANGLE_MAX
        };
    }

    public static getMouseSensitivityLimits(): {min: number, max: number} {
        return {
            min: FlyControls.MOUSE_SENSITIVITY_MIN,
            max: FlyControls.MOUSE_SENSITIVITY_MAX
        };
    }

    public init() {
        super.init();

        // this.oldTexturesResolutionLimitRT = renderNodes.TextureNode.texturesResolutionSwitchLimitRT;
        // Renderer.setTexturesResolutionSwitchLimitRT(256.0);
    }

    // reset mode specialities
    public destroy() {
        // Renderer.setTexturesResolutionSwitchLimitRT(this.oldTexturesResolutionLimitRT);
    }

    public onBoundingBoxUpdated(aBoundingBox: BoundingBox, aBoundingBoxForNPC: BoundingBox) {
        super.onBoundingBoxUpdated(aBoundingBox, aBoundingBoxForNPC);

        this._collisionCameraDown.onBoundingBoxUpdated(aBoundingBox, aBoundingBoxForNPC);
        this._collisionCameraForward.onBoundingBoxUpdated(aBoundingBox, aBoundingBoxForNPC);

        this.speedMax = this.computeMaxSpeed(aBoundingBox);
    }

    public getControlsType(): EViewControls {
        return EViewControls.FLY;
    }

    public onMousePress(x: number, y: number, buttonState: number) {
        if (!this.isEnabled()) {
            return false;
        }

        // if ( this._pointerLockUsed ) {
        //     if (buttonState & 1) { // left mb
        //         this.moveFlagMap[this.goUp] = true;
        //     }
        //     if (buttonState & 2) { // right mb
        //         this.moveFlagMap[this.goDown] = true;
        //     }
        // }
        this._mouseState.set(0, 0, x, y, x, y, buttonState, true);
    }

    public onMouseRelease(x: number, y: number, buttonState: number, prevButton: number) {
        if (!this.isEnabled()) {
            return false;
        }

        // if (prevButton & 1) { // left mb
        //     this.moveFlagMap[this.goUp] = false;
        // }
        // if (prevButton & 2) { // right mb
        //     this.moveFlagMap[this.goDown] = false;
        // }

        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, buttonState, false);
    }

    public onDrag(x: number, y: number, delta: number, buttonState: number) {
        if (!this.isEnabled()) {
            return false;
        }

        let dx = x - this._mouseState.lastX;
        let dy = y - this._mouseState.lastY;

        this._mouseState.isMoving = true;

        if (buttonState & 1 || buttonState & 2) { // left and right
            this.rotateCameraTarget(this._view.horizontalPixelsToRad(dx), this._view.verticalPixelsToRad(dy));
        }

        this._mouseState.dx = dx;
        this._mouseState.dy = dy;

        this._mouseState.lastX = x;
        this._mouseState.lastY = y;

        this._view.viewpointChanged();
    }

    public onWheel(x: number, y: number, delta: number, buttonState: number): boolean {
        return true;
    }

    public computeMaxSpeed(aBoundingBox: BoundingBox): number {
        // select max and divide
        let max = aBoundingBox.getDiameter(); // the diameter is the max here
        let speed = max * this.CDWK_FP_MODE_MAX_SPEED_RATIO;

        // check limits for small models
        speed = Math.max(speed, this.CDWK_FP_MODE_MIN_MAX_SPEED); // min max speed correction

        // Debug.logInfo('max speed: '+ speed);
        return speed;
    }

    public emitSpeed() {
        let sp = (this.step + 1) / this.CDWK_FP_MODE_NUM_STEPS * 100.0;

        Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_flyingSpeedChanged", {
            speedPercent: sp,
            value: this.step
        });
    }

    public onMouseIn(x: number, y: number, buttonState: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, 0, false);

        return true;
    }

    public onMouseOut(x: number, y: number, buttonState: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, 0, false);

        return true;
    }

    public onDoubleClick(x: number, y: number): boolean {
        return true;
    }

    public onTouchStart(x: number, y: number, numTouches: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        super.onTouchStart(x, y, numTouches);

        // set start coords by default (in case there is no touch move)
        this._touchCoords.x = x;
        this._touchCoords.y = y;

        this._touchDoubleTapTimeout = window.setTimeout(() => {
            let deltaX = Math.abs(this._touchCoords.x - x);
            let deltaY = Math.abs(this._touchCoords.y - y);

            if (deltaX <= FlyControls.TOUCH_WALK_TOUCH_RADIUS &&
                deltaY <= FlyControls.TOUCH_WALK_TOUCH_RADIUS) {
                this.moveFlagMap[this.goForward] = true;
            }
        }, FlyControls.TOUCH_WALK_TIME_DELAY);

        return true;
    }

    public onTouchEnd(x: number, y: number, remainingTouch: number, aDraggedDuringTouch: boolean): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        super.onTouchEnd(x, y, remainingTouch, aDraggedDuringTouch);

        this.moveFlagMap[this.goForward] = false;
        clearTimeout(this._touchDoubleTapTimeout);

        return true;
    }

    public onTouchMove(x: number, y: number, numTouches: number, delta: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        super.onTouchMove(x, y, numTouches, delta);

        this._touchCoords.x = x;
        this._touchCoords.y = y;

        return true;
    }

    public onKeyDown(key: string) {
        if (!this.isEnabled()) {
            return false;
        }

        switch (key) {
            case  "Up": // up arr
            case  "ArrowUp": // up arr
            case  "W": // W
            case  "w": // w
                if (!this._ctrlDown) {
                    this.moveFlagMap[this.goForward] = true;
                }
                break;

            case  "Down": // down arr
            case  "ArrowDown": // down arr
            case  "S": // S
            case  "s": // s
                if (!this._ctrlDown) {
                    this.moveFlagMap[this.goBack] = true;
                }
                break;

            case  "Left": // left arr
            case  "ArrowLeft": // left arr
            case  "A": // A
            case  "a": // a
                if (!this._ctrlDown) {
                    this.moveFlagMap[this.goLeft] = true;
                }
                break;

            case  "Right": // right arr
            case  "ArrowRight": // right arr
            case  "D": // D
            case  "d": // d
                if (!this._ctrlDown) {
                    this.moveFlagMap[this.goRight] = true;
                }
                break;

            case  "PageUp": // page up
                this.moveFlagMap[this.goUp] = true;
                break;

            case "PageDown": // page down
                this.moveFlagMap[this.goDown] = true;
                break;

            case "Shift": // shift
                if (!this._shiftDown) {
                    if (this.step + 1 < this.CDWK_FP_MODE_NUM_STEPS) {
                        this.setSpeed(this.step + 1);
                    }
                    this._shiftDown = true;
                }
                break;

            case "Control": // ctrl
                this._ctrlDown = true;
                break;

            default:
                break;
        }

    }

    public onKeyUp(key: string) {
        if (!this.isEnabled()) {
            return false;
        }

        switch (key) {
            case  "Up": // up arr
            case  "ArrowUp": // up arr
            case  "W": // W
            case  "w": // w
                this.moveFlagMap[this.goForward] = false;
                break;

            case  "Down": // down arr
            case  "ArrowDown": // down arr
            case  "S": // S
            case  "s": // s
                this.moveFlagMap[this.goBack] = false;
                break;

            case  "Left": // left arr
            case  "ArrowLeft": // left arr
            case  "A": // A
            case  "a": // a
                this.moveFlagMap[this.goLeft] = false;
                break;

            case  "Right": // right arr
            case  "ArrowRight": // right arr
            case  "D": // D
            case  "d": // d
                this.moveFlagMap[this.goRight] = false;
                break;

            case  "PageUp": // page up
                this.moveFlagMap[this.goUp] = false;
                break;

            case  "PageDown": // page down
                this.moveFlagMap[this.goDown] = false;
                break;

            case "Shift": // shift
                if (this._shiftDown) {
                    if (this.step > 0) {
                        this.setSpeed(this.step - 1);
                    }
                    this._shiftDown = false;
                }
                break;

            case "Control": // ctrl
                this._ctrlDown = false;
                break;

            default:
                break;
        }
    }

    public onKeyPress(key: string) {
        if (!this.isEnabled()) {
            return false;
        }

        switch (key) {
            // cdwk
            /*
                    case  43: // + (incr. speed)
                        let evt = document.createEvent("Event");
                        evt.initEvent("mousewheel", true, true);
                        evt.wheelDelta = 120; // the 120 is default wheel value used in all major browsers?
                        this._view.getHTMLCanvas().dispatchEvent(evt);
                        break;
                    case  45: // - (decr. speed)
                        let evt = document.createEvent("Event");
                        evt.initEvent("mousewheel", true, true);
                        evt.wheelDelta = -120; // the -120 is default wheel value used in all major browsers?
                        this._view.getHTMLCanvas().dispatchEvent(evt);
                        break;
            */
            case "Enter": // ENTER
                // case 27: // ESC // problem to catch and used to cancel fullscreen mode
                // this._view.resetViewMode();
                // notify gui
                break;
            case "i": // i show debug log dialog
            case "d": // d show debug log dialog
                // toggleShowDebugConsole = !ebug.toggleShowDebugConsole;
                // activate(toggleShowDebugConsole);
                break;
            case "/":  // / show properties hud
                this._sceneAPI.getStateViewer().toggle();
                break;
            default:
                break;
        }
    }

    public tick(): void {
        super.tick();

        this.speed = this.speedValues[this.step];
        // this.emitSpeed(); emit speed only when it has changed

        let speed = 0;
        if (this._acceleration < 0.0) {
            speed = 0;
        } else if (this._acceleration > 0.0) {
            let now = Date.now();
            if (this._lastTick === 0) {
                this.setSpeed(0);
            } else {
                let dt = now - this._lastTick;
                this.speed = this.speed + (this._acceleration * dt);
                speed = this.speed;

                if (speed > this.speedValues[this.step]) {
                    this._acceleration = 0;
                    speed = this.speedValues[this.step];
                }
            }
            this._lastTick = now;
            this.emitSpeed();
        } else { // acc === 0
            speed = this.speedValues[this.step];
        }

        // Debug.logInfo(speed);
        // Debug.logInfo(this.speed);

        this._moveCamera(speed);
    }

    // handles an event on the canvas for the pointerlock example
    public onMovePointerLock(e: any) {
        super.onMovePointerLock(e);

        this.rotateCameraTarget(this._view.horizontalPixelsToRad(e.dx), this._view.verticalPixelsToRad(e.dy));

        this._view.viewpointChanged();
    }

    public getRealSpeed(): number {
        return this.speed;
    }

    // Set user defined speed step. <0, SPEED_STEPS_MAX).
    public setSpeed(aStep: number) {
        this.step = Math.max(FlyControls.SPEED_STEPS_MIN, Math.min(aStep, FlyControls.SPEED_STEPS_MAX - 1));
        this.speed = this.speedValues[this.step];

        this.emitSpeed();
    }

    // Set user defined view angle in degrees. Limits <45, 90> checked.
    public setViewAngle(aViewAngle: number) {
        this.openingAngle = Math.max(FlyControls.VIEW_ANGLE_MIN, Math.min(aViewAngle, FlyControls.VIEW_ANGLE_MAX));
        this._camera.setFieldOfView(degToRad(this.openingAngle));

        this._collisionCameraForward.setFieldOfView(degToRad(this.openingAngle));

        this._viewMode.scheduleRedraw();
    }

    // Set user defined mouse sensitivity. Limits <1, 5> checked.
    public setMouseSensitivity(aValue: number) {
        let va = Math.max(FlyControls.MOUSE_SENSITIVITY_MIN, Math.min(aValue, FlyControls.MOUSE_SENSITIVITY_MAX));
        this.mouseSensitivity = FlyControls.MOUSE_SENSITIVITY_MAX - va + 1;
    }

    public setAcceleration(aAcceleration: number) {
        this._acceleration = aAcceleration;
    }

    protected _initCameraParams() {
        super._initCameraParams();

        this.openingAngle = 60.0;

        this._camera.setFieldOfView(degToRad(this.openingAngle));

        // this is for non logarithmic rendering, for logarithmic rendering the near plane is effectively zero
        this._camera.forceNearValue(0.1);
    }

    private _moveCamera(aSpeed: number) {
        let newPos = this._camera.getCameraPosition();
        let newTarget = this._camera.getCameraTarget();

        let step = aSpeed * this._stepFactor;

        let viewDir = this._camera.getCameraDirection();

        if (this.step === 0) {
            vec3.set(this.dx, 0, 0, 0);
            vec3.set(this.dy, 0, 0, 0);
        }

        vec3.scale(viewDir, viewDir, step);
        vec3.add(newPos, newPos, viewDir);
        vec3.add(newTarget, newTarget, viewDir);

        this._camera.setCameraPositionAndTarget(newPos, newTarget);

        this._view.viewpointChanged();
    }
}
